import Functions from '@client/style/Functions';
import { Breakpoints } from '@client/style/Variables';
import gsap, { Power2 } from 'gsap';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, type JSX } from 'react';
import styled from 'styled-components';
import RoundButtonWithAnimation from './RoundButtonWithAnimation';
import Cross3 from '@client/assets/svgs/cross-3.svg';

const OuterContainer = styled.div<{ backgroundColor?: string }>`
	position: relative;
	display: flex;
	justify-content: center;
	width: 100%;
	height: 100%;
	background-color: ${props => (props.backgroundColor ? props.backgroundColor : 'inherit')};
	pointer-events: all;
	padding: 20px;

	opacity: 0;
`;

const TransitionContainer = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
	transform: translateY(100%);
`;

const Container = styled.div<{ $bg: string }>`
	display: flex;
	flex-direction: column;
	position: relative;
	max-width: 600px;
	width: 100%;
	height: 50em;
	max-height: 100%;
	pointer-events: all;
	background-color: ${props => props.$bg};

	${Functions.breakpoint(Breakpoints.tablet)} {
		height: 65em;
	}
`;

const CloseButton = styled(RoundButtonWithAnimation)`
	position: absolute;
	top: 20px;
	right: 20px;
	width: 40px;
	height: 40px;
	pointer-events: all;
	z-index: 2;
`;

const InnerContainer = styled.div`
	flex: 0 0 auto;
	position: relative;
	padding: 20px;
	padding-bottom: 80px;
	padding-top: 80px;

	${Functions.breakpoint(Breakpoints.tablet)} {
		padding-left: 50px;
		padding-right: 50px;
	}
`;

/**
 * Illustrations
 */
const IllustrationsContainer = styled.div`
	flex: 1 1 auto;
	position: relative;
`;

const IlluBranches = styled.div`
	position: absolute;
	inset: 0;
	overflow: hidden;
`;

const BranchesImg = styled.img`
	width: 100%;
	object-fit: cover;
	object-position: top;
	position: absolute;
	bottom: 0;
	max-height: 35em;
	height: 90%;

	${Functions.breakpoint(Breakpoints.tablet)} {
		max-height: 40em;
	}
`;

const HandsImg = styled.img`
	width: auto;
	object-fit: contain;
	object-position: bottom;
	position: absolute;
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
	max-height: 40em;
	height: calc(100% + 2em);

	${Functions.breakpoint(Breakpoints.tablet)} {
		max-height: 50em;
	}
`;

interface IProps {
	className?: string;
	children?: JSX.Element | JSX.Element[];
	backgroundColor: string;
	dimColor?: string;
	onCloseButtonClick?: () => void;
}

export type ImperativeLoginPromptFrame = {
	close: (onComplete?: () => void) => void;
};

export const LoginPromptOverlayFrame = forwardRef<ImperativeLoginPromptFrame, IProps>(
	function LoginPromptOverlayFrame(props, ref) {
		const innerContainer = useRef<HTMLDivElement | null>(null);
		const outerContainer = useRef<HTMLDivElement | null>(null);

		useEffect(() => {
			if (outerContainer.current) {
				gsap.to(outerContainer.current, {
					opacity: 1,
					ease: Power2.easeOut,
					duration: 0.15,
				});
			}

			if (innerContainer.current) {
				gsap.to(innerContainer.current, {
					y: '0%',
					ease: Power2.easeOut,
					duration: 0.5,
				});
			}
		}, [innerContainer.current, outerContainer.current]);

		const handleClose = useCallback(
			(onComplete?: () => void) => {
				if (!innerContainer.current) {
					onComplete?.();
					return;
				} else {
					gsap.to(innerContainer.current, {
						y: '100%',
						ease: Power2.easeIn,
						duration: 0.5,
						onComplete,
					});
				}

				if (outerContainer.current) {
					gsap.to(outerContainer.current, {
						opacity: 0,
						ease: Power2.easeIn,
						duration: 0.15,
						delay: 0.5 - 0.15,
					});
				}
			},
			[innerContainer.current, outerContainer.current]
		);

		const onCloseButtonClick = useCallback(() => {
			return handleClose(props.onCloseButtonClick);
		}, [handleClose, props.onCloseButtonClick]);

		useImperativeHandle(
			ref,
			() => ({
				close: onComplete => {
					return handleClose(onComplete);
				},
			}),
			[handleClose]
		);

		return (
			<OuterContainer backgroundColor={props.dimColor} ref={outerContainer}>
				<TransitionContainer ref={innerContainer}>
					<Container $bg={props.backgroundColor}>
						{props.onCloseButtonClick && (
							<CloseButton size={40} interaction={onCloseButtonClick}>
								<Cross3 color='#262C2E' />
							</CloseButton>
						)}

						<InnerContainer>{props.children && props.children}</InnerContainer>

						<IllustrationsContainer>
							<IlluBranches>
								<BranchesImg src='/assets/images/sign-up-branches.png' />
							</IlluBranches>
							<HandsImg src='/assets/images/lovehands.png' />
						</IllustrationsContainer>
					</Container>
				</TransitionContainer>
			</OuterContainer>
		);
	}
);
